/**
 * Centralised responsive typography.
 *
 * The product is designed desktop-first; on smaller screens (< Tailwind `lg`)
 * every text and heading size should step down one — occasionally two —
 * notches. Rather than sprinkle `lg:` prefixes across hundreds of call sites,
 * the rule lives here and is applied inside the base `Text` / `Heading`
 * components (both the explicit `className` and the `size` variant maps).
 *
 *   `text-{size}`  ->  `text-{smaller} lg:text-{size}`
 *
 * Tiny sizes (`text-xs`, `text-2xs`) are intentionally left alone so labels and
 * captions stay legible on phones. Colour (`text-primary-500`), alignment
 * (`text-center`) and arbitrary (`text-[13px]`) classes are never matched.
 */

const STEP_DOWN: Record<string, string> = {
  "text-sm": "text-xs",
  "text-base": "text-sm",
  "text-md": "text-sm",
  "text-lg": "text-base",
  "text-xl": "text-lg",
  "text-2xl": "text-xl",
  "text-3xl": "text-2xl",
  "text-4xl": "text-2xl",
  "text-5xl": "text-3xl",
  "text-6xl": "text-3xl",
};

// Matches a *bare* size token only: preceded by start/whitespace (so variant
// prefixes like `lg:`/`hover:` are skipped) and followed by whitespace/end.
const BARE_SIZE_TOKEN =
  /(^|\s)(text-(?:sm|base|md|lg|xl|2xl|3xl|4xl|5xl|6xl))(?=\s|$)/g;

/** Rewrite bare font-size utilities in a className string to responsive form. */
export const responsiveType = (className?: string): string | undefined => {
  if (!className) return className;
  return className.replace(
    BARE_SIZE_TOKEN,
    (_match, pre: string, cls: string) => {
      const down = STEP_DOWN[cls];
      return down ? `${pre}${down} lg:${cls}` : `${pre}${cls}`;
    }
  );
};

/** Apply {@link responsiveType} to every value of a tva size-variant map. */
export const responsiveSizes = <T extends Record<string, string>>(
  sizes: T
): T =>
  Object.fromEntries(
    Object.entries(sizes).map(([key, cls]) => [key, responsiveType(cls) ?? cls])
  ) as T;
